import { buffers } from 'redux-saga';
import { actionChannel, call, cancelled, delay, flush, fork, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { getAvailableSlackMessages, sendSlackMessage } from 'services/slackService';
import { resolveTransactionId, transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import {
    SLACK_LATE_RESULT_TTL_MS,
    SLACK_SEND_RESULT_TIMEOUT_MS,
    SLACK_SEND_STATUS,
    buildSlackResultSnackbar,
    buildSlackSendingSnackbar,
    slackSendingSnackbarKey,
    getSlackErrorMessage,
    isSameSlackLogId,
    isSlackOutcomeUnknownError,
    isSlackRejectedError,
    toSlackResultStatus,
} from 'utils/slackMessages';

import { slackActions } from './slackSlice';
import { selectSlackSlice } from './slackSelectors';

const selectSendOf = (transactionId) => (state) => selectSlackSlice(state).sendByTransactionId[transactionId];

function* takeLeadingPerKey(type, saga, keySelector) {
    const activeTasks = {};
    while (true) {
        const action = yield take(type);
        const key = keySelector(action);
        if (key == null) continue;
        const running = activeTasks[key];
        if (running && running.isRunning()) continue;
        activeTasks[key] = yield fork(saga, action);
    }
}

export function* fetchSlackMessagesSaga({ payload: { transactionId } }) {
    try {
        const items = yield call(getAvailableSlackMessages, transactionId);
        yield put(slackActions.fetchMessagesSuccess({ transactionId, items: Array.isArray(items) ? items : [] }));
    } catch (err) {
        // The Slack action stays hidden; it must never get in the way of processing the transaction.
        console.log(`fetchSlackMessagesSaga(): transactionId=${transactionId} error=${getSlackErrorMessage(err)}`);
        yield put(slackActions.fetchMessagesFailed({ transactionId }));
    }
}

let lastSendNumber = 0;

// Numbers the sends of this tab, so each "Sending..." snackbar has its own key.
export const nextSlackSendNumber = () => {
    lastSendNumber += 1;
    return lastSendNumber;
};

// Sends the FE ended as UNKNOWN while the backend may still deliver them, so their real result can still be shown:
// keyed by log id when the POST answered, by transaction when it did not. acceptedLogIds are the ids of every accepted
// send, so a result of a known send is never taken for an abandoned one of the same transaction. Both expire.
const abandonedSends = new Map();
const acceptedLogIds = new Map();

const pruneExpired = (map, now) => map.forEach((entry, key) => entry.expiresAt <= now && map.delete(key));

export function rememberAcceptedSend(logId, now = Date.now()) {
    acceptedLogIds.set(String(logId), { expiresAt: now + SLACK_LATE_RESULT_TTL_MS });
}

export function rememberAbandonedSend({ transactionId, logId = null, subject, channelName }, now = Date.now()) {
    const key = logId != null ? `log:${logId}` : `tx:${transactionId}`;
    abandonedSends.set(key, { transactionId, subject, channelName, expiresAt: now + SLACK_LATE_RESULT_TTL_MS });
}

// Takes (removes and returns) the abandoned send a websocket result belongs to, or null.
export function claimAbandonedSend(result, { transactionSending = false } = {}, now = Date.now()) {
    pruneExpired(abandonedSends, now);
    pruneExpired(acceptedLogIds, now);
    const claim = (key) => {
        const send = abandonedSends.get(key) ?? null;
        abandonedSends.delete(key);
        return send;
    };
    const byLogId = `log:${result?.logId}`;
    if (abandonedSends.has(byLogId)) return claim(byLogId);
    // By transaction only for a result of no send the FE knows: not of one being sent now (its result can beat the POST
    // response) and not of one already accepted with this id.
    if (transactionSending || acceptedLogIds.has(String(result?.logId))) return null;
    const byTransaction = `tx:${result?.transactionId}`;
    return abandonedSends.has(byTransaction) ? claim(byTransaction) : null;
}

// After a POST with no response: a buffered result of the transaction that belongs to no accepted send is this send's
// own, delivered while its response was lost or late.
export const findUnmatchedResult = (buffered, transactionId) =>
    buffered
        .map((action) => action.payload)
        .find((result) => isSameSlackLogId(result?.transactionId, transactionId) && !acceptedLogIds.has(String(result?.logId))) ?? null;

export function* finishSend(transactionId, status, { subject, channelName, sendNumber, detail = null }) {
    yield put(snackbarActions.closeSnackbar(slackSendingSnackbarKey(transactionId, sendNumber)));
    yield put(slackActions.sendFinished({ transactionId, status, detail }));
    yield put(snackbarActions.enqueueSnackbar(buildSlackResultSnackbar(status, { subject, channelName, detail })));
}

function* takeSendResult(results, logId) {
    while (true) {
        const { payload } = yield take(results);
        // Results of other sends (other transactions, earlier sends) are left to the late-result saga.
        if (isSameSlackLogId(payload?.logId, logId)) return payload;
    }
}

// Waits on the send's result channel for the .SlackSendResultMessage of logId. Returns null when none arrives in time.
export function* waitForSendResult(results, logId) {
    const { result } = yield race({ result: call(takeSendResult, results, logId), timeout: delay(SLACK_SEND_RESULT_TIMEOUT_MS) });
    return result ?? null;
}

// The send is async: the POST only accepts it ({ id, status: PENDING }) and the result comes over the desk websocket,
// up to ~30s later (the backend retries FAILED deliveries itself, up to 3 attempts). The "Sending..." snackbar stays
// until then. After FAILED / UNKNOWN the Agent retries from the dialog. Submit does not wait for any of this.
export function* sendSlackMessageSaga({ payload }) {
    const { transactionId, subject, channelName, request } = payload;
    const sendNumber = yield call(nextSlackSendNumber);
    // Snackbar data shared by the "Sending..." snackbar and every result below.
    const names = { subject, channelName, sendNumber };
    // Opened before the POST: the websocket can deliver the result before the POST response, and the channel keeps it.
    const results = yield actionChannel(slackActions.sendResultReceived.type, buffers.expanding());

    yield put(snackbarActions.enqueueSnackbar(buildSlackSendingSnackbar(transactionId, names)));

    try {
        const response = yield call(sendSlackMessage, transactionId, request);
        const logId = response?.id;
        if (logId == null) {
            console.log(`sendSlackMessageSaga(): transactionId=${transactionId} accepted without an id, the result cannot be matched`);
            yield call(finishSend, transactionId, SLACK_SEND_STATUS.UNKNOWN, names);
            return;
        }
        yield call(rememberAcceptedSend, logId);

        const result = yield call(waitForSendResult, results, logId);
        if (!result) {
            // Missed websocket message: by now the backend has closed the send as UNKNOWN, so a retry is accepted.
            console.log(`sendSlackMessageSaga(): transactionId=${transactionId} logId=${logId} no result after ${SLACK_SEND_RESULT_TIMEOUT_MS}ms`);
            yield call(rememberAbandonedSend, { transactionId, logId, subject, channelName });
            yield call(finishSend, transactionId, SLACK_SEND_STATUS.UNKNOWN, names);
            return;
        }
        yield call(finishSend, transactionId, toSlackResultStatus(result.status), { ...names, detail: result.detail ?? null });
    } catch (err) {
        // No response at all (timeout, connection dropped): the request may still have reached Slack.
        if (isSlackOutcomeUnknownError(err)) {
            console.log(`sendSlackMessageSaga(): transactionId=${transactionId} no response to the send request, error=${err?.message}`);
            const buffered = yield flush(results);
            const result = findUnmatchedResult(buffered, transactionId);
            if (result) {
                yield call(finishSend, transactionId, toSlackResultStatus(result.status), { ...names, detail: result.detail ?? null });
                return;
            }
            yield call(rememberAbandonedSend, { transactionId, subject, channelName });
            yield call(finishSend, transactionId, SLACK_SEND_STATUS.UNKNOWN, { ...names, detail: err?.message || null });
            return;
        }

        const message = getSlackErrorMessage(err);
        console.log(`sendSlackMessageSaga(): transactionId=${transactionId} status=${err?.response?.status} error=${message}`);
        // 400: rejected, nothing was posted (already sent, in progress, too long, ...); show the backend's message.
        const status = isSlackRejectedError(err) ? SLACK_SEND_STATUS.REJECTED : SLACK_SEND_STATUS.FAILED;
        yield call(finishSend, transactionId, status, { ...names, detail: message || null });
    } finally {
        results.close();
        // Cancelled while waiting for the backend (e.g. the root saga stopped): without this the persistent "Sending..."
        // snackbar would never close and SENDING would keep Slack disabled until a page reload. The request
        // may still have reached Slack, so it ends as UNKNOWN and the Agent can check the channel and retry.
        if (yield cancelled()) {
            console.log(`sendSlackMessageSaga(): transactionId=${transactionId} cancelled before the result arrived`);
            yield call(finishSend, transactionId, SLACK_SEND_STATUS.UNKNOWN, names);
        }
    }
}

// A result that arrives after its send already ended as UNKNOWN on the FE: shows the real outcome, so the Agent does not
// retry a message that was delivered, and keeps Retry for one that failed.
export function* handleLateSendResultSaga({ payload: result }) {
    const send = yield select(selectSendOf(result?.transactionId));
    const abandoned = yield call(claimAbandonedSend, result, { transactionSending: send?.status === SLACK_SEND_STATUS.SENDING });
    if (!abandoned) return;

    const status = toSlackResultStatus(result.status);
    console.log(`handleLateSendResultSaga(): transactionId=${abandoned.transactionId} logId=${result.logId} late result status=${status}`);
    // UNKNOWN is what the Agent was already shown.
    if (status === SLACK_SEND_STATUS.UNKNOWN) return;

    const detail = result.detail ?? null;
    yield put(slackActions.lateResultReceived({ transactionId: abandoned.transactionId, status, detail }));
    yield put(
        snackbarActions.enqueueSnackbar(buildSlackResultSnackbar(status, { subject: abandoned.subject, channelName: abandoned.channelName, detail }))
    );
}

function* cleanupSlackStateSaga({ payload }) {
    const { transactionId, closeAllPanels } = payload || {};
    if (closeAllPanels) {
        yield put(slackActions.resetAll());
        return;
    }
    if (transactionId != null) yield put(slackActions.removeTransaction(transactionId));
}

// The panel of a send that was closed while running opens again (e.g. reopened): its FAILED / UNKNOWN result is kept
// for Retry again instead of being dropped with the closed panel.
export function* restoreSendOnPanelOpenSaga({ payload }) {
    const transactionId = resolveTransactionId(payload);
    const send = yield select(selectSendOf(transactionId));
    if (send?.panelClosed) yield put(slackActions.panelReopened(transactionId));
}

export function* slackSaga() {
    yield fork(takeLeadingPerKey, slackActions.fetchMessagesStart.type, fetchSlackMessagesSaga, (action) => action.payload?.transactionId);
    yield fork(takeLeadingPerKey, slackActions.sendStart.type, sendSlackMessageSaga, (action) => action.payload?.transactionId);
    yield takeEvery(slackActions.sendResultReceived.type, handleLateSendResultSaga);
    yield takeEvery(transactionActions.handleNewTransaction.type, restoreSendOnPanelOpenSaga);
    yield takeEvery(transactionActions.closeTransactionPanel.type, cleanupSlackStateSaga);
}
